import axios from 'axios';
import { parse, stringify } from 'qs';
import {
  BUSINESS_HEADER_REFUSED_EVENT,
  isBusinessHeaderRefused,
} from '@/consts/businessForbidden';
import { clearSession, setSession } from '../auth/utils';
import { PATH_AUTH } from '../routes/paths';
import {
  dispatchImpersonationRevoked,
  isImpersonationAccessError,
} from '../auth/impersonationSession';
import {
  dispatchSessionCredentialsMissing,
  isMissingCredentialsError,
} from '../auth/sessionCredentials';
import { logApiError } from '../utils/apiErrorLogger';

const methodsToProtect = ['post', 'put', 'patch', 'delete'];

/**
 * Teto de tempo para qualquer requisição. Existe porque sem ele uma conexão
 * pendurada (rede ou backend sem resposta) nunca rejeita: a query fica em
 * carregamento para sempre, e a UI não tem erro nenhum para mostrar — é o
 * "loading infinito sem estado de erro" da CKTPL-1078. Com timeout, o axios
 * aborta com ECONNABORTED, que `utils/apiErrorLogger.js` já classifica como
 * `timeout`, e o `onError` global do React Query (routes/index.js) passa a
 * notificar normalmente.
 *
 * 60s é deliberadamente generoso: o objetivo é matar a requisição pendurada,
 * não apertar o tempo de resposta legítimo do backend.
 */
const DEFAULT_TIMEOUT_MS = 60_000;

/**
 * Download e exportação são a exceção legítima: a resposta só chega depois de
 * o backend gerar o arquivo (`/reports/**\/export/`, boleto, CSV de alunos),
 * o que pode passar de um minuto sem que nada esteja errado. Em vez de
 * espalhar `timeout` por cada service, o critério fica aqui: requisição que
 * espera binário não tem teto de tempo.
 */
const isBinaryDownload = (config) =>
  config?.responseType === 'blob' || config?.responseType === 'arraybuffer';

const applyTimeout = (config) => {
  if (isBinaryDownload(config)) {
    config.timeout = 0;
  }

  return config;
};

const ensureTrailingSlash = (url) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;

  const pathMatch = url.match(/^[^?#]*/);
  const path = pathMatch ? pathMatch[0] : url;
  const rest = url.slice(path.length);

  if (path.endsWith('/')) return url;

  return `${path}/${rest}`;
};

const setDefaultHeader = (client, header, value) => {
  if (value) {
    client.defaults.headers.common[header] = value;
  } else {
    delete client.defaults.headers.common[header];
  }
};

const GuestHttp = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

const AuthenticatedHttp = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: {
    encode: parse,
    serialize: (params) => stringify(params, { arrayFormat: 'comma' }),
  },
});

const BaasHttp = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: {
    encode: parse,
    serialize: (params) => stringify(params, { arrayFormat: 'comma' }),
  },
});

const setDefaultHeaderForAuthenticatedClients = (header, value) => {
  [AuthenticatedHttp, BaasHttp].forEach((client) => {
    setDefaultHeader(client, header, value);
  });
};

let csrfTokenCache = null;
let csrfTokenRequest = null;
let csrfCacheGeneration = 0;

const generateCSRF = async ({ forceRefresh = false } = {}) => {
  if (!forceRefresh && csrfTokenCache) {
    return csrfTokenCache;
  }

  if (!forceRefresh && csrfTokenRequest) {
    return csrfTokenRequest;
  }

  const generation = csrfCacheGeneration;
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  csrfTokenRequest = axios
    .get(`${baseUrl}/get-csrf-token/`, { withCredentials: true })
    .then((csrfResponse) => {
      const { csrfToken } = csrfResponse.data;
      // um fetch iniciado antes de clearCSRFCache() carrega token do secret
      // antigo; se a geração mudou, ele não pode sobrescrever o cache novo
      if (generation === csrfCacheGeneration) {
        csrfTokenCache = csrfToken || null;
      }
      return csrfToken || null;
    })
    .finally(() => {
      if (generation === csrfCacheGeneration) {
        csrfTokenRequest = null;
      }
    });

  return csrfTokenRequest;
};

// O backend rotaciona o secret de CSRF no login (register/login/logout);
// qualquer token cacheado antes dessa fronteira passa a ser inválido.
const clearCSRFCache = () => {
  csrfCacheGeneration += 1;
  csrfTokenCache = null;
  csrfTokenRequest = null;
};

const applyTrailingSlash = (config) => {
  config.url = ensureTrailingSlash(config.url);
  return config;
};

const applyAuthentication = (config) => {
  config.headers = config.headers || {};

  const accessToken = localStorage.getItem('accessToken');
  const shouldApplyImpersonation = !config.skipImpersonation;
  const impersonatedUserId = shouldApplyImpersonation
    ? localStorage.getItem('impersonatedUserId') ||
    AuthenticatedHttp.defaults.headers.common['x-cakto-account-id'] ||
    BaasHttp.defaults.headers.common['x-cakto-account-id']
    : null;

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  } else {
    delete config.headers.Authorization;
  }

  if (impersonatedUserId) {
    config.headers['x-cakto-account-id'] = String(impersonatedUserId);
  } else {
    delete config.headers['x-cakto-account-id'];
  }

  return config;
};

const applyCSRFProtection = async (config) => {
  config.headers = config.headers || {};

  const method = config.method?.toLowerCase();

  if (methodsToProtect.includes(method)) {
    const csrfToken = await generateCSRF();
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken;
    }
  }

  return config;
};

const rejectHttpError = (error) => {
  logApiError(error, { httpClient: 'guest' });
  return Promise.reject(error);
};

const handleImpersonationHttpError = (error) => {
  if (localStorage.getItem('impersonatedUserId') && isImpersonationAccessError(error)) {
    clearAccountHeader();
    dispatchImpersonationRevoked();
  }
};

// A escrita foi recusada porque o negócio guardado não é mais da pessoa (CBK-572).
// Quem reage é o BusinessContext, que volta ao padrão e avisa; o axios só anuncia,
// do mesmo jeito que já faz com a impersonação revogada. Nada é repetido.
const handleBusinessHeaderRefused = (error) => {
  if (typeof window !== 'undefined' && isBusinessHeaderRefused(error)) {
    window.dispatchEvent(new CustomEvent(BUSINESS_HEADER_REFUSED_EVENT));
  }
};

// A requisição saiu sem credencial que o backend aceite. Pode ser a sessão que acabou com a
// tela aberta ou uma rota que só aceita JWT para quem entrou pelo SSO: o axios só anuncia, e
// quem decide é o SessionProvider, que confirma no /user/ antes de encerrar.
const handleMissingCredentials = (error) => {
  if (isMissingCredentialsError(error)) {
    dispatchSessionCredentialsMissing();
  }
};

/**
 * Renovação em voo, compartilhada por todas as requisições que tomaram token vencido
 * ao mesmo tempo: uma tela que dispara várias consultas com o token vencido gera
 * um único POST /token/refresh/, e todas repetem com o par novo.
 */
let refreshPromise = null;

const refreshAccessToken = () => {
  if (refreshPromise) return refreshPromise;

  const refresh = localStorage.getItem('refreshToken');
  if (!refresh) return Promise.resolve(null);

  refreshPromise = GuestHttp.post('/token/refresh/', { refresh })
    .then(({ data }) => {
      const access = data?.access || null;
      if (access) {
        // O backend gira o refresh (ROTATE_REFRESH_TOKENS): guardar o novo, e manter
        // o atual quando a resposta não trouxer um.
        setSession({ accessToken: access, refreshToken: data?.refresh ?? refresh });
      }
      return access;
    })
    .catch(() => null)
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

const redirectToLogin = () => {
  const { pathname, search } = window.location;
  const next = encodeURIComponent(`${pathname}${search}`);
  window.location.assign(`${PATH_AUTH.login}?next=${next}`);
};

// Nas rotas com a autenticação padrão do backend a sessão vem antes do JWT, e o DRF
// devolve 403 para o token vencido (a classe de sessão não declara `authenticate_header`).
// O `code` do simplejwt é o que separa esse 403 da permissão negada e da trava de validação.
const isExpiredAccessToken = (error) => {
  const { status, data } = error.response || {};
  return status === 401 || (status === 403 && data?.code === 'token_not_valid');
};

const handleAuthenticatedHttpError = async (error) => {
  logApiError(error, { httpClient: 'authenticated' });

  const status = error.response?.status;
  const original = error.config;

  handleImpersonationHttpError(error);
  handleBusinessHeaderRefused(error);
  handleMissingCredentials(error);

  // Só o token vencido entra na renovação; o resto do 403 (permissão, validação de
  // cadastro) vai para a tela. A revogação da impersonação também pode vir como 401, mas
  // o token é válido: o que caiu foi o acesso à conta, já tratado acima.
  if (!isExpiredAccessToken(error) || isImpersonationAccessError(error)) {
    return Promise.reject(error);
  }

  const hadSession = Boolean(localStorage.getItem('accessToken'));

  // `_retry` impede laço: uma requisição só se repete uma vez.
  if (original && !original._retry) {
    original._retry = true;

    const access = await refreshAccessToken();
    if (access) {
      // O header do config vence o default da instância: sem esta linha a repetição
      // sairia com o token velho.
      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${access}`;
      return AuthenticatedHttp.request(original);
    }
  }

  // Sem renovação, o 403 não leva ao login daqui: ele também chega em rota pública para
  // quem tem token velho guardado, e ali a pessoa continua vendo a página como visitante.
  // Na rota protegida, quem leva ao login é o AuthGuard.
  if (status === 403) {
    return Promise.reject(error);
  }

  // Sem como renovar, a sessão do token acabou de verdade. Sem token guardado não há o que
  // encerrar daqui: é o visitante numa rota pública, ou quem entrou pelo SSO, que só tem a
  // sessão Django e toma este 401 com ela viva numa rota que só aceita JWT. Limpar o storage
  // aí apagaria o negócio selecionado e a impersonação de quem segue logado.
  if (hadSession) {
    clearSession();
    redirectToLogin();
  }

  return Promise.reject(error);
};

const handleBaasHttpError = (error) => {
  logApiError(error, { httpClient: 'baas' });
  handleImpersonationHttpError(error);
  handleBusinessHeaderRefused(error);
  return Promise.reject(error);
};

GuestHttp.interceptors.request.use((config) => {
  applyTrailingSlash(config);
  applyTimeout(config);
  return config;
});

const BUSINESS_STORAGE_KEY = 'activeBusinessId';

const BUSINESS_HEADER = 'X-Cakto-Business';

// O negócio selecionado vai em toda chamada autenticada do painel, inclusive na
// conta digital (CBK-571): sem ele o backend não sabe qual negócio está ativo, e
// os dados não acompanhavam o seletor (CBK-563). Separado do setAccountHeader de
// proposito: aquele e a chave da impersonation (x-cakto-account-id) e o erro dele
// dispara a revogacao de acesso.
const applyBusinessContext = (config) => {
  config.headers = config.headers || {};

  const businessId = localStorage.getItem(BUSINESS_STORAGE_KEY);

  if (businessId) {
    config.headers[BUSINESS_HEADER] = String(businessId);
  } else {
    delete config.headers[BUSINESS_HEADER];
  }

  return config;
};

const setBusinessContext = (businessId) => {
  const normalizedBusinessId = businessId ? String(businessId) : null;

  if (normalizedBusinessId) {
    localStorage.setItem(BUSINESS_STORAGE_KEY, normalizedBusinessId);
  } else {
    localStorage.removeItem(BUSINESS_STORAGE_KEY);
  }
};

const clearBusinessContext = () => {
  setBusinessContext(null);
};

AuthenticatedHttp.interceptors.request.use(async (config) => {
  applyTrailingSlash(config);
  applyTimeout(config);
  applyAuthentication(config);
  applyBusinessContext(config);
  await applyCSRFProtection(config);
  return config;
});

AuthenticatedHttp.interceptors.response.use(
  (response) => response,
  handleAuthenticatedHttpError
);

BaasHttp.interceptors.request.use(async (config) => {
  applyTrailingSlash(config);
  applyTimeout(config);
  applyAuthentication(config);
  applyBusinessContext(config);
  await applyCSRFProtection(config);
  return config;
});

GuestHttp.interceptors.response.use((response) => response, rejectHttpError);
BaasHttp.interceptors.response.use((response) => response, handleBaasHttpError);

const setAuthorizationHeader = (authorization) => {
  setDefaultHeaderForAuthenticatedClients('Authorization', authorization);
};

const clearAuthorizationHeader = () => {
  setAuthorizationHeader(null);
};

const setAccountHeader = (userId) => {
  const normalizedUserId = userId ? String(userId) : null;

  if (normalizedUserId) {
    localStorage.setItem('impersonatedUserId', normalizedUserId);
  } else {
    localStorage.removeItem('impersonatedUserId');
  }

  setDefaultHeaderForAuthenticatedClients('x-cakto-account-id', normalizedUserId);
};

const clearAccountHeader = () => {
  setAccountHeader(null);
};

export { AuthenticatedHttp, GuestHttp, BaasHttp, generateCSRF, clearCSRFCache };
export { setAuthorizationHeader, clearAuthorizationHeader, setAccountHeader, clearAccountHeader };
export { BUSINESS_STORAGE_KEY, setBusinessContext, clearBusinessContext };
export default AuthenticatedHttp;
